import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { api } from '../../../lib/api';
import { snakeCaseKeys } from '../../../shared/helpers';
import { useNotification } from '../../../shared/Notification/useNotification';
import { broadcastMessageKey } from './useFetchBroadcastMessages';

const recipientFieldUrl = (broadcastMessageUuid, id) => {
  const base = `/api/client_admin/broadcast_messages/${broadcastMessageUuid}/recipient_fields`;

  return id ? `${base}/${id}` : base;
};

// Pure cache helpers. Saving a recipient field must not refetch the
// broadcast: StepContainer swaps the step for a spinner while the query is
// fetching, which unmounts the editor and reads as a full page refresh.
export const upsertRecipientField = (fields = [], field) =>
  fields.some(item => item.id === field.id)
    ? fields.map(item => (item.id === field.id ? field : item))
    : [...fields, field];

export const removeRecipientField = (fields = [], id) =>
  fields.filter(item => item.id !== id);

const updateCachedRecipientFields = (
  queryClient,
  broadcastMessageUuid,
  updater,
) =>
  queryClient.setQueryData(
    broadcastMessageKey(broadcastMessageUuid),
    cached => {
      const broadcastMessage = cached?.broadcastMessage || {};

      return {
        ...cached,
        broadcastMessage: {
          ...broadcastMessage,
          recipientFields: updater(broadcastMessage.recipientFields || []),
        },
      };
    },
  );

const recipientFieldPayload = ({ name, key, skipMissing, rows }) => ({
  ...snakeCaseKeys({
    name,
    key,
    ...(skipMissing === undefined ? {} : { skipMissing }),
  }),
  // rows is authoritative for the field when present; omitted entirely so a
  // partial patch (e.g. toggling skipMissing) never touches saved values.
  ...(rows === undefined ? {} : { rows: rows.map(row => snakeCaseKeys(row)) }),
});

export const useCreateRecipientFieldMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('broadcasts');
  const { showError } = useNotification();

  const { mutate: createRecipientFieldMutation, isLoading } = useMutation(
    ({ broadcastMessageUuid, ...values }) =>
      api({
        method: 'post',
        url: recipientFieldUrl(broadcastMessageUuid),
        data: { recipient_field: recipientFieldPayload(values) },
      }),
    {
      onSuccess: ({ data }, { broadcastMessageUuid }) => {
        updateCachedRecipientFields(queryClient, broadcastMessageUuid, fields =>
          upsertRecipientField(fields, data.recipientField),
        );
      },
      onError: error => {
        const errors = error?.response?.data?.errors;

        showError({
          content: errors?.[0] || t('recipientFieldDialog.saveError'),
        });
      },
    },
  );

  return { createRecipientFieldMutation, isLoading };
};

export const useUpdateRecipientFieldMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('broadcasts');
  const { showError } = useNotification();

  const { mutate: updateRecipientFieldMutation, isLoading } = useMutation(
    ({ broadcastMessageUuid, id, ...values }) =>
      api({
        method: 'patch',
        url: recipientFieldUrl(broadcastMessageUuid, id),
        data: { recipient_field: recipientFieldPayload(values) },
      }),
    {
      onSuccess: ({ data }, { broadcastMessageUuid }) => {
        updateCachedRecipientFields(queryClient, broadcastMessageUuid, fields =>
          upsertRecipientField(fields, data.recipientField),
        );
      },
      onError: error => {
        const errors = error?.response?.data?.errors;

        showError({
          content: errors?.[0] || t('recipientFieldDialog.saveError'),
        });
      },
    },
  );

  return { updateRecipientFieldMutation, isLoading };
};

export const useDestroyRecipientFieldMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('broadcasts');
  const { showError } = useNotification();

  const { mutate: destroyRecipientFieldMutation, isLoading } = useMutation(
    ({ broadcastMessageUuid, id }) =>
      api({
        method: 'delete',
        url: recipientFieldUrl(broadcastMessageUuid, id),
      }),
    {
      onSuccess: (_response, { broadcastMessageUuid, id }) => {
        updateCachedRecipientFields(queryClient, broadcastMessageUuid, fields =>
          removeRecipientField(fields, id),
        );
      },
      onError: error => {
        const errors = error?.response?.data?.errors;

        showError({
          content: errors?.[0] || t('recipientFieldDialog.deleteError'),
        });
      },
    },
  );

  return { destroyRecipientFieldMutation, isLoading };
};

// CSV import matching (BroadcastRecipientFields::MatchRows). Doesn't touch
// the cache itself — the dialog applies `matched` rows into its own table
// state via "Apply to table".
export const useMatchRecipientRowsMutation = () => {
  const { t } = useTranslation('broadcasts');
  const { showError } = useNotification();

  const { mutate: matchRecipientRowsMutation, isLoading } = useMutation(
    ({ broadcastMessageUuid, rows }) =>
      api({
        method: 'post',
        url: `${recipientFieldUrl(broadcastMessageUuid)}/match`,
        data: { rows: rows.map(row => snakeCaseKeys(row)) },
      }),
    {
      onError: error => {
        const errors = error?.response?.data?.errors;

        showError({
          content: errors?.[0] || t('recipientFieldDialog.matchError'),
        });
      },
    },
  );

  return { matchRecipientRowsMutation, isLoading };
};
